import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMyOrderDetailQuery } from '@/entities/order';
import { isReviewNotFound, useMyReviewQuery } from '@/entities/review';
import { ReviewForm, useSaveReview } from '@/features/review-write';
import { MY_REVIEWS_PATH } from '@/shared/config';
import { SitePageShell } from '@/shared/ui';
import { ReviewPageHeader } from '@/widgets/review-page-header';
import { ReviewPageNotice } from '@/widgets/review-page-notice';
import { ReviewTargetCard } from '@/widgets/review-target-card';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

const BACK_TO_MY_REVIEWS = { to: MY_REVIEWS_PATH, label: '내 후기로 가기' };

/** 후기 수정(/my/reviews/:reviewId/edit) — 숨김 처리된 후기는 고칠 수 없다. */
export const ReviewEditPage = (): ReactElement => {
  const { reviewId = '' } = useParams();
  const parsed = normalizedUuidSchema.safeParse(reviewId);
  const navigate = useNavigate();
  const review = useMyReviewQuery(parsed.success ? parsed.data : '', { enabled: parsed.success });
  const save = useSaveReview();
  const current = review.data;
  // 내 후기 응답에는 썸네일이 없어 연결된 주문 상세에서 견적 디자인 썸네일을 가져온다(실패하면 자리표시).
  const order = useMyOrderDetailQuery(current?.orderId ?? '', { enabled: current !== undefined });

  const body = ((): ReactElement => {
    if (!parsed.success || isReviewNotFound(review.error)) {
      return (
        <ReviewPageNotice kind="error" message="후기를 찾을 수 없어요." link={BACK_TO_MY_REVIEWS} />
      );
    }
    if (review.isPending) {
      return <ReviewPageNotice kind="loading" message="후기를 불러오는 중이에요" />;
    }
    if (current === undefined) {
      return (
        <ReviewPageNotice
          kind="error"
          message="후기를 불러오지 못했어요. 잠시 후 다시 시도해 주세요."
          onRetry={() => void review.refetch()}
        />
      );
    }
    if (current.status === 'HIDDEN') {
      return <ReviewPageNotice kind="error" message="숨김 처리된 후기는 수정할 수 없어요." />;
    }
    // 폼이 처음 보여 준 수정 시각을 그대로 보낸다 — 배경 재조회로 값이 바뀌면 key 가 달라져 폼도 새 내용으로 다시 시작한다.
    const expectedUpdatedAt = current.updatedAt;
    return (
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-16">
        <div className="lg:order-2">
          <ReviewTargetCard
            productName={current.productName}
            thumbnailUrl={order.data?.thumbnailUrl ?? null}
            detail={[
              current.colorName,
              current.printMethodName,
              `${current.quantity.toLocaleString('ko-KR')}개 구매`,
            ]
              .filter((part) => part !== null)
              .join(' · ')}
          />
        </div>
        <ReviewForm
          key={expectedUpdatedAt}
          initial={current}
          submitLabel="수정"
          onSubmit={async (values) => {
            await save.update({ reviewId, expectedUpdatedAt, ...values }, current.productId);
            navigate(MY_REVIEWS_PATH);
          }}
        />
      </div>
    );
  })();

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <ReviewPageHeader title="후기 수정" />
        {body}
      </div>
    </SitePageShell>
  );
};
